import { gql } from '@apollo/client'

const BUILDER_ATTRIBUTES = gql`
  fragment builderAttributes on Builder {
    id
    createdAt
    updatedAt
    archivedAt

    company {
      name
    }
    builderMetadata

    logoKey
    logoUrl
    requiredTickets {
      id
      name
      shortName
    }

    subscription
    isTrial
    trialExpiresAt
    clerkOrganizationId
    defaultTimezone
    defaultLocale
    defaultLanguage
  }
`

const BUILDER_LIST_ATTRIBUTES = gql`
  fragment builderListAttributes on Builder {
    id
    createdAt

    projectsCount
    adminUsersCount
    stripeIdentifier
    hubspotDealIdentifier

    isDemo
    isSeeded
    isTrial
    trialExpiresAt
    subscription

    company {
      name
    }
  }
`

export const GET_BUILDER = gql`
  query builder_UI {
    builder {
      ...builderAttributes
    }
  }
  ${BUILDER_ATTRIBUTES}
`

export const GET_BUILDER_WHITELABEL = gql`
  query builderWhitelabel_UI($name: String!) {
    builderWhitelabel(name: $name) {
      logoUrl
      hideBuildPassLogos
    }
  }
`

export const GET_BUILDER_WITH_PROCORE = gql`
  query builderWithProcore_UI {
    builder {
      ...builderAttributes
      procoreConfig {
        id
        isDev
        procoreCompanyId
      }
    }
  }
  ${BUILDER_ATTRIBUTES}
`

export const GET_BUILDER_WITH_PROCORE_AND_PROJECTS = gql`
  query builderWithProcoreAndProjects_UI {
    builder {
      ...builderAttributes
      procoreConfig {
        id
        procoreCompanyId
        isDev
      }
      projects {
        id
        procoreId
      }
    }
  }
  ${BUILDER_ATTRIBUTES}
`

export const GET_BUILDER_METADATA = gql`
  query builderMetadata_UI {
    builder {
      builderMetadata
    }
  }
`
export const GET_USER_BUILDER = gql`
  query userBuilder_UI {
    user {
      adminUser {
        builder {
          ...builderAttributes
          builderSubcontractor {
            company {
              abn
            }
          }
        }
      }
    }
  }
  ${BUILDER_ATTRIBUTES}
`

export const UPDATE_BUILDER = gql`
  mutation updateBuilder_UI($id: String!, $input: UpdateBuilderInput!) {
    updateBuilder(id: $id, input: $input) {
      ...builderAttributes
    }
  }
  ${BUILDER_ATTRIBUTES}
`

export const UPDATE_BUILDER_METADATA = gql`
  mutation updateBuilderMetadata_UI($input: UpdateBuilderMetadataInput!) {
    updateBuilderMetadata(input: $input) {
      ...builderAttributes
    }
  }
  ${BUILDER_ATTRIBUTES}
`

export const UPDATE_BUILDER_SUBBIE = gql`
  mutation updateBuilderSubcontractorData_UI(
    $input: UpdateBuilderSubcontractorDataInput!
  ) {
    updateBuilderSubcontractorData(input: $input) {
      id
    }
  }
`

export const GET_BUILDER_LOGO_UPLOAD_URL = gql`
  query getPresignedUrlForBuilderLogo_UI(
    $id: String!
    $filename: String!
    $filesize: String!
  ) {
    getPresignedUrl(
      input: {
        id: $id
        type: BUILDER
        filename: $filename
        filesize: $filesize
        filetype: "logo"
      }
    ) {
      url
      key
    }
  }
`

export const GET_SA_BUILDERS_WITH_AGG = gql`
  query saBuildersWithAgg_UI(
    $filter: BuilderFilterInput
    $sort: BuilderSortInput
    $pagination: PaginationInput
  ) {
    saBuilders(filter: $filter, sort: $sort, pagination: $pagination) {
      ...builderListAttributes

      builderSettings {
        externalLinks {
          externalLinkCuid
          type
          homeUrl
          linkUrl
          id
        }
      }
    }
    saBuildersAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${BUILDER_LIST_ATTRIBUTES}
`

export const SA_GET_BUILDERS = gql`
  query saBuilders_UI {
    saBuilders {
      id
      logoUrl

      company {
        name
      }
      isDemo
    }
  }
`

export const SA_GET_BUILDER_WITH_MODULES = gql`
  query saBuilderWithModules_UI($id: String!) {
    saBuilder(id: $id) {
      ...builderAttributes
      modules
      projectsCount
      adminUsersCount
      stripeIdentifier
      hubspotDealIdentifier

      isDemo
      isSeeded
      isTrial
      trialExpiresAt
      subscription

      builderSettings {
        hideMedical
        externalLinks {
          externalLinkCuid
          type
          homeUrl
          linkUrl
          id
        }
      }
    }
  }
  ${BUILDER_ATTRIBUTES}
`

export const SA_GET_BUILDER_AUDIT_SUMMARY = gql`
  query saBuilderAuditSummary_UI($id: String!) {
    saBuilder(id: $id) {
      auditLogSummary {
        last30Days
        last60Days
        last90Days
      }
    }
  }
`

export const SA_ARCHIVE_BUILDER = gql`
  mutation saArchiveBuilder_UI($id: String!) {
    saArchiveBuilder(id: $id)
  }
`

export const SA_GET_BUILDER_MODULES_LIST = gql`
  query saBuilderModulesList_UI($id: String!) {
    saBuilder(id: $id) {
      id
      company {
        name
      }
      modules
    }
  }
`

export const SA_UPDATE_BUILDER_MODULES = gql`
  mutation saUpdateBuilderModules_UI($id: String!, $input: ToggleModuleInput!) {
    saUpdateBuilderModules(id: $id, input: $input)
  }
`

export const CREATE_BUILDER = gql`
  mutation createBuilder_UI($input: CreateBuilderInput!) {
    createBuilder(input: $input) {
      id
    }
  }
`

export const SA_UPDATE_BUILDER = gql`
  mutation saUpdateBuilder_UI($id: String!, $input: SaUpdateBuilderInput!) {
    saUpdateBuilder(id: $id, input: $input) {
      id
    }
  }
`

export const UPDATE_BUILDER_QUESTIONNAIRE = gql`
  mutation updateBuilderQuestionnaire_UI(
    $input: UpdateBuilderQuestionnaireInput!
  ) {
    updateBuilderQuestionnaire(input: $input) {
      questionnaire
    }
  }
`

export const SWITCH_SUPER_ADMIN_BUILDER = gql`
  mutation switchSuperAdminBuilder_UI($builderId: String!) {
    switchSuperAdminBuilder(builderId: $builderId) {
      id
    }
  }
`
export const SYNC_BUILDER_WITH_DATA_PIPELINE = gql`
  mutation syncBuilderWithDataPipeline_UI($builderId: String!) {
    syncBuilderWithDataPipeline(builderId: $builderId) {
      renewalDate
      createdAt
      currentPeriodStart

      status
      product
      features

      billingInterval
      billingIntervalCount
      billingAmount

      trialStart
      trialEnd

      discount {
        durationType
        durationInMonths
        discountType
        amountOff
        percentOff
      }
    }
  }
`

export const BULK_SYNC_BUILDER_WITH_DATA_PIPELINE = gql`
  mutation bulkSyncBuilderWithDataPipeline_UI($builderIds: [String!]!) {
    bulkSyncBuilderWithDataPipeline(builderIds: $builderIds) {
      successfullySyncedIds
      failedSyncedIds
    }
  }
`
